import './BranchRegistrationWorkflowManageForm.pcss';

import {
  CaretDownOutlined,
  CaretUpOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  EyeOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { Button, Form, Select } from 'custom-test-antd';
import { Formik } from 'formik';
import { FC, useEffect, useState, useCallback } from 'react';

import { BranchRegistrationWorkflowDto, Portal } from 'api/organization/onboardingTypes';
import { InputPasswordWrapper, InputWrapper } from 'utils/form-helpers/antd-formik';

import { BranchRegistrationWorkflowSearchResult } from './BranchRegistrationWorkflowSearchResult';
import { getPortalDescription } from './utils';
import { getValidationSchema } from '../utils/validations';

interface BranchRegistrationWorkflowManageFormProps {
  branchesTaken: string[];
  branchRegistrationWorkflow: BranchRegistrationWorkflowDto;
  branchRegistrationWorkflowCanBeManaged: boolean;
  onUpdateBranchRegistrationWorkflowCredentials: (values: {
    id: string;
    portal: Portal;
    login: string;
    password: string;
  }) => Promise<void>;
  onExcludeBranchFromRegistrationWorkflow: (id: string, provider: string) => Promise<void>;
  onIncludeBranchToRegistrationWorkflow: (id: string, provider: string) => Promise<void>;
  onRemoveBranchRegistrationWorkflow: (id: string) => Promise<void>;
}

export const BranchRegistrationWorkflowManageForm: FC<BranchRegistrationWorkflowManageFormProps> = ({
  branchesTaken,
  branchRegistrationWorkflow,
  branchRegistrationWorkflowCanBeManaged,
  onUpdateBranchRegistrationWorkflowCredentials,
  onExcludeBranchFromRegistrationWorkflow,
  onIncludeBranchToRegistrationWorkflow,
  onRemoveBranchRegistrationWorkflow,
}) => {
  const isExpandable = branchRegistrationWorkflowCanBeManaged && !branchRegistrationWorkflow.areCredentialsValid;
  const [isExpanded, setIsExpanded] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  // Auto-expand when credentials become invalid
  useEffect(() => {
    if (isExpandable && !branchRegistrationWorkflow.areCredentialsValid) {
      setIsExpanded(true);
    }
  }, [isExpandable, branchRegistrationWorkflow.areCredentialsValid]);

  useEffect(() => {
    if (!isExpandable && isExpanded) {
      setIsExpanded(false);
    }
    // Clear validation state when form becomes non-expandable or is collapsed
    if (!isExpandable || !isExpanded) {
      setIsValidating(false);
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
        setDebounceTimeout(null);
      }
    }
  }, [isExpandable, isExpanded, debounceTimeout]);

  const [isRemovingBranchRegistrationWorkflow, setIsRemovingBranchRegistrationWorkflow] = useState(false);

  const handleFieldValidation = useCallback(async (
    fieldName: string,
    value: string,
    validateField: any,
    values: { id: string; portal: Portal; login: string; password: string },
    submitForm: () => Promise<void>,
  ) => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    // Show validation spinner
    setIsValidating(true);

    const timeout = setTimeout(async () => {
      try {
        await validateField(fieldName);

        // Auto-submit if both fields have values
        if (values.login.trim() && values.password.trim()) {
          await submitForm();
        }
      } catch (error) {
        // Validation failed, do nothing
      } finally {
        // Hide validation spinner
        setIsValidating(false);
      }
    }, 1000);

    setDebounceTimeout(timeout);
  }, [debounceTimeout]);

  const handleSubmit = useCallback(async (values: { id: string; portal: Portal; login: string; password: string }) => {
    await onUpdateBranchRegistrationWorkflowCredentials(values);
  }, [onUpdateBranchRegistrationWorkflowCredentials]);

  return (
    <div key={branchRegistrationWorkflow.id} className="credential-card">
      <div
        className="credential-header"
        style={{ cursor: isExpandable ? 'pointer' : 'default' }}
        onClick={() => {
          setIsExpanded(isExpandable ? !isExpanded : false);
        }}
      >
        <div className="facility-info">
          <Select
            value={branchRegistrationWorkflow.portal}
            disabled
            className="facility-select"
            suffixIcon={<CaretDownOutlined />}
          >
            <Select.Option value={branchRegistrationWorkflow.portal}>{branchRegistrationWorkflow.portal}</Select.Option>
          </Select>
          <div className="login-text">
            {branchRegistrationWorkflow.login}
          </div>
        </div>
        <div className="header-actions">
          {isExpandable
            && (isExpanded
              ? <CaretUpOutlined className="expand-icon" />
              : <CaretDownOutlined className="expand-icon" />)}
          <Button
            type="text"
            className="remove-button"
            disabled={!branchRegistrationWorkflowCanBeManaged || branchRegistrationWorkflow.isCompleted}
            onClick={async (e) => {
              e.stopPropagation();
              setIsRemovingBranchRegistrationWorkflow(true);
              await onRemoveBranchRegistrationWorkflow(branchRegistrationWorkflow.id);
              setIsRemovingBranchRegistrationWorkflow(false);
            }}
            loading={isRemovingBranchRegistrationWorkflow}
          >
            Remove
          </Button>
        </div>
      </div>
      <div className="credential-content">
        <div className="facility-description">{getPortalDescription(branchRegistrationWorkflow.portal)}</div>
        {branchRegistrationWorkflow.areCredentialsValid ? (
          <>
            <div className="credential-status valid">
              <div className="status-row">
                <CheckCircleFilled className="valid-icon" />
                Valid Credential
              </div>
            </div>
            <BranchRegistrationWorkflowSearchResult
              branchesTaken={branchesTaken}
              branchRegistrationWorkflow={branchRegistrationWorkflow}
              branchRegistrationWorkflowCanBeManaged={branchRegistrationWorkflowCanBeManaged}
              onExcludeBranchFromRegistrationWorkflow={onExcludeBranchFromRegistrationWorkflow}
              onIncludeBranchToRegistrationWorkflow={onIncludeBranchToRegistrationWorkflow}
            />
          </>
        ) : (
          <div className="credential-status invalid">
            <div className="status-row">
              <CloseCircleFilled className="invalid-icon" />
              Invalid Credentials
            </div>
          </div>
        )}
        {isExpanded && (
          <Formik
            initialValues={{
              id: branchRegistrationWorkflow.id,
              portal: branchRegistrationWorkflow.portal,
              login: branchRegistrationWorkflow.login,
              password: '',
            }}
            validationSchema={getValidationSchema(branchRegistrationWorkflow.portal)}
            onSubmit={handleSubmit}
          >
            {({ values, validateField, submitForm, isSubmitting }) => (
              <Form layout="vertical">
                <InputWrapper
                  name="login"
                  size="large"
                  antFieldProps={{
                    label: branchRegistrationWorkflow.portal === Portal.Riayati ? 'License' : 'Username',
                  }}
                  placeholder={
                  branchRegistrationWorkflow.portal === Portal.Riayati ? 'Enter license' : 'Enter username'
                }
                  onChange={(e) => {
                    handleFieldValidation('login', e.target.value, validateField, values, submitForm);
                  }}
                />
                <InputPasswordWrapper
                  name="password"
                  placeholder={branchRegistrationWorkflow.portal === Portal.Riayati ? 'Enter API key' : '••••••'}
                  size="large"
                  antFieldProps={{
                    label: branchRegistrationWorkflow.portal === Portal.Riayati ? 'API Key' : 'Password',
                  }}
                  iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeOutlined />)}
                  onChange={(e) => {
                    handleFieldValidation('password', e.target.value, validateField, values, submitForm);
                  }}
                />
                {isValidating && (
                  <div className="validation-indicator">
                    <LoadingOutlined />
                    {' '}
                    Validating...
                  </div>
                )}
                {isSubmitting && <div className="submitting-indicator">Submitting...</div>}
              </Form>
            )}
          </Formik>
        )}
      </div>
    </div>
  );
};
